import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getCustomers, getCustomerHistory, deleteCustomer } from "../lib/api.ts";
import { SearchIcon } from "../components/Icon.tsx";
import { decodeEntities } from "../lib/decode-entities.ts";
import { CustomerFormModal } from "../components/CustomerFormModal.tsx";
import { useIsMobile } from "../lib/use-is-mobile.ts";

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  notes?: string;
  total_appointments: number;
  last_appointment_at: string | null;
}

interface HistoryItem {
  id: string;
  start_at: string;
  service_name: string;
  staff_name: string;
  status: string;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function CustomerHistory({ slug, customerId }: { slug: string; customerId: string }) {
  const [history, setHistory] = useState<{ active: HistoryItem[]; archived: HistoryItem[] }>({ active: [], archived: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCustomerHistory(slug, customerId)
      .then((data) => {
        const d = data as { active?: HistoryItem[]; archived?: HistoryItem[] };
        setHistory({ active: d.active ?? [], archived: d.archived ?? [] });
      })
      .catch(() => { /* ignore */ })
      .finally(() => setLoading(false));
  }, [slug, customerId]);

  if (loading) return <div style={{ fontSize: 12.5, color: "var(--ink-faint)", padding: "8px 0" }}>Loading history…</div>;
  if (history.active.length === 0 && history.archived.length === 0)
    return <div style={{ fontSize: 12.5, color: "var(--ink-faint)", padding: "8px 0" }}>No appointment history.</div>;

  return (
    <div>
      {history.active.length > 0 && <HistorySection title="Active" items={history.active} />}
      {history.archived.length > 0 && <HistorySection title="Past" items={history.archived} />}
    </div>
  );
}

function HistorySection({ title, items }: { title: string; items: HistoryItem[] }) {
  return (
    <div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10,
          fontWeight: 500,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--ink-faint)",
          marginTop: 10,
          marginBottom: 2,
        }}
      >
        {title}
      </div>
      <ul style={{ margin: 0, listStyle: "none", padding: 0 }}>
        {items.map((h, i) => (
          <li
            key={h.id ?? i}
            style={{
              padding: "10px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderTop: i === 0 ? "none" : "1px solid var(--rule)",
            }}
          >
            <div>
              <span style={{ fontWeight: 600, fontSize: 13.5, color: "var(--ink)" }}>{decodeEntities(h.service_name)}</span>
              {h.staff_name && (
                <span style={{ fontSize: 12.5, color: "var(--ink-soft)" }}> · {h.staff_name}</span>
              )}
              <div style={{ fontSize: 12, color: "var(--ink-faint)", marginTop: 2 }}>
                {new Date(h.start_at).toLocaleString()}
              </div>
            </div>
            <span className={`badge-${h.status === "completed" ? "green" : h.status === "cancelled" ? "red" : "blue"}`}>
              {h.status}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Customers() {
  const { slug } = useParams<{ slug: string }>();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Customer | "new" | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    getCustomers(slug, debouncedSearch)
      .then((data) => setCustomers(data as Customer[]))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const isMobile = useIsMobile();

  return (
    <div>
      <section className="zd-hero" style={{ marginBottom: 32 }}>
        <div className="zd-hero-text">
          <div className="zd-hero-kicker">
            Directory <span className="dot">·</span> {customers.length} {customers.length === 1 ? "person" : "people"}
          </div>
          <h1 className="zd-hero-headline">Customers</h1>
          <p className="zd-hero-lead">Everyone who's booked. Click a row to see their history; click a phone number to call.</p>
        </div>
        <div className="zd-hero-actions">
          <div style={{ position: "relative", flex: "1 1 auto" }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-faint)", pointerEvents: "none" }}>
              <SearchIcon size={16} />
            </span>
            <input
              type="search"
              placeholder="Search by name, email, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input zd-page-search"
              style={{ paddingLeft: 36 }}
            />
          </div>
          <button type="button" className="btn-primary" style={{ whiteSpace: "nowrap" }} onClick={() => setEditing("new")}>
            Add customer
          </button>
        </div>
      </section>

      {error && <div className="alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ padding: "48px 0", textAlign: "center", color: "var(--ink-faint)", fontSize: 14 }}>
          Loading…
        </div>
      ) : customers.length === 0 ? (
        <div className="zd-empty">
          <h3>{search ? "No matches." : "No customers yet."}</h3>
          <p>
            {search
              ? "Try a partial name, the country-coded phone digits, or just a domain (@gmail.com)."
              : "Once people book, they'll appear here with visit count and history."}
          </p>
        </div>
      ) : isMobile ? (
        <div className="zd-card-list">
          {customers.map((c) => (
            <div
              key={c.id}
              className="zd-card"
              onClick={() => setSelectedId(selectedId === c.id ? null : c.id)}
              style={{ cursor: "pointer" }}
            >
              <div className="zd-card-row">
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                  <span style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--accent-soft)", color: "var(--accent-ink)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 600, border: "1px solid var(--rule)", flexShrink: 0 }}>
                    {initials(c.name)}
                  </span>
                  <span style={{ fontWeight: 600, fontSize: 15, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                </div>
                <span style={{ fontSize: 13, color: "var(--ink-soft)", whiteSpace: "nowrap" }}>
                  {c.total_appointments ?? 0} {(c.total_appointments ?? 0) === 1 ? "visit" : "visits"}
                </span>
              </div>
              {(c.phone || c.email) && (
                <div style={{ fontSize: 13, color: "var(--ink-soft)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {[c.phone, c.email].filter(Boolean).join(" · ")}
                </div>
              )}
              <div className="zd-card-actions">
                {c.phone && (
                  <a href={`tel:${c.phone}`} className="btn-secondary" style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                    Call
                  </a>
                )}
                <button type="button" className="btn-secondary" onClick={(e) => { e.stopPropagation(); setEditing(c); }}>
                  Edit
                </button>
              </div>
              {selectedId === c.id && slug && (
                <div style={{ borderTop: "1px solid var(--rule)", paddingTop: 8 }}>
                  <CustomerHistory slug={slug} customerId={c.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            background: "var(--paper-raised)",
            borderRadius: 14,
            boxShadow: "var(--shadow-sm)",
            overflow: "hidden",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--paper-well)" }}>
                {[
                  { label: "Customer", show: true, align: "left" },
                  { label: "Email", show: true, align: "left", hide: "sm" },
                  { label: "Phone", show: true, align: "left", hide: "md" },
                  { label: "Visits", show: true, align: "left" },
                  { label: "Last visit", show: true, align: "left", hide: "lg" },
                ].map((h, i) => (
                  <th
                    key={i}
                    className={h.hide === "sm" ? "hidden sm:table-cell" : h.hide === "md" ? "hidden md:table-cell" : h.hide === "lg" ? "hidden lg:table-cell" : ""}
                    style={{
                      padding: "14px 18px",
                      textAlign: h.align as "left",
                      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                      fontSize: 10.5,
                      fontWeight: 500,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: "var(--ink-faint)",
                    }}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <CustomerRow
                  key={c.id}
                  c={c}
                  selected={selectedId === c.id}
                  onSelect={() => setSelectedId(selectedId === c.id ? null : c.id)}
                  slug={slug}
                  onEdit={() => setEditing(c)}
                  onChanged={load}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && slug && (
        <CustomerFormModal
          slug={slug}
          initial={
            editing === "new"
              ? undefined
              : {
                  id: editing.id,
                  name: editing.name,
                  email: editing.email,
                  phone: editing.phone,
                  notes: editing.notes,
                }
          }
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

function CustomerRow({
  c,
  selected,
  onSelect,
  slug,
  onEdit,
  onChanged,
}: {
  c: Customer;
  selected: boolean;
  onSelect: () => void;
  slug: string | undefined;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Reset the delete affordance when the row collapses so reopening it never
  // lands the user mid-confirmation or shows a stale error.
  useEffect(() => {
    if (!selected) {
      setConfirmDelete(false);
      setDeleteError(null);
    }
  }, [selected]);

  async function handleDelete() {
    if (!slug) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteCustomer(slug, c.id);
      onChanged();
    } catch (err) {
      const code = (err as { error?: { code?: string } })?.error?.code;
      if (code === "HAS_APPOINTMENTS") {
        setDeleteError("This customer has appointments and can't be deleted.");
      } else {
        const msg = (err as { error?: { message?: string } })?.error?.message;
        setDeleteError(msg ?? "Could not delete customer. Please try again.");
      }
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  return (
    <>
      <tr
        onClick={onSelect}
        style={{ borderTop: "1px solid var(--rule)", cursor: "pointer", background: selected ? "var(--paper-well)" : undefined }}
      >
        <td style={{ padding: "14px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "var(--accent-soft)",
                color: "var(--accent-ink)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11.5,
                fontWeight: 600,
                border: "1px solid var(--rule)",
                flexShrink: 0,
              }}
            >
              {initials(c.name)}
            </span>
            <span style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>{c.name}</span>
          </div>
        </td>
        <td className="hidden sm:table-cell" style={{ padding: "14px 18px", fontSize: 13, color: "var(--ink-soft)" }}>
          {c.email || <span style={{ color: "var(--ink-faint)" }}>—</span>}
        </td>
        <td className="hidden md:table-cell" style={{ padding: "14px 18px", fontSize: 13 }}>
          {c.phone ? (
            <a
              href={`tel:${c.phone}`}
              onClick={(e) => e.stopPropagation()}
              style={{ color: "var(--ink-soft)", textDecoration: "none" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "var(--accent-ink)"; e.currentTarget.style.textDecoration = "underline"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "var(--ink-soft)"; e.currentTarget.style.textDecoration = "none"; }}
            >
              {c.phone}
            </a>
          ) : (
            <span style={{ color: "var(--ink-faint)" }}>—</span>
          )}
        </td>
        <td style={{ padding: "14px 18px", fontSize: 14, color: "var(--ink)", fontFeatureSettings: "'tnum'", fontWeight: 500 }}>
          {c.total_appointments ?? 0}
        </td>
        <td className="hidden lg:table-cell" style={{ padding: "14px 18px", fontSize: 13, color: "var(--ink-soft)", fontFeatureSettings: "'tnum'" }}>
          {c.last_appointment_at
            ? new Date(c.last_appointment_at).toLocaleDateString()
            : <span style={{ color: "var(--ink-faint)" }}>—</span>}
        </td>
      </tr>
      {selected && slug && (
        <tr>
          <td colSpan={5} style={{ padding: "0 18px 18px", background: "var(--paper-well)" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginTop: 8,
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 10.5,
                  fontWeight: 500,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "var(--ink-faint)",
                }}
              >
                Booking history
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {!confirmDelete ? (
                  <>
                    <button type="button" className="btn-secondary" style={{ fontSize: 12.5, padding: "5px 12px" }} onClick={onEdit}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ fontSize: 12.5, padding: "5px 12px", color: "var(--status-cancelled, #c0392b)" }}
                      onClick={() => { setDeleteError(null); setConfirmDelete(true); }}
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: 12.5, color: "var(--ink-soft)" }}>Delete this customer?</span>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ fontSize: 12.5, padding: "5px 12px" }}
                      disabled={deleting}
                      onClick={() => setConfirmDelete(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      style={{ fontSize: 12.5, padding: "5px 12px" }}
                      disabled={deleting}
                      onClick={() => void handleDelete()}
                    >
                      {deleting ? "Deleting…" : "Confirm delete"}
                    </button>
                  </>
                )}
              </div>
            </div>
            {deleteError && (
              <div className="alert-error" style={{ margin: "8px 0" }}>{deleteError}</div>
            )}
            <CustomerHistory slug={slug} customerId={c.id} />
          </td>
        </tr>
      )}
    </>
  );
}
